import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useManaSymbols } from '../hooks/useManaSymbols';
import './DecksPage.css';

const API = `${process.env.REACT_APP_API_URL || 'https://api.guardianbazaar.com'}/api`;

const FORMATS = [
  'Standard','Pioneer','Modern','Legacy','Vintage',
  'Commander','Pauper','Explorer','Historic','Brawl','Draft','Casual'
];

const STATUSES: { value: string; label: string; color: string }[] = [
  { value: 'draft',     label: '📝 Draft',     color: '#606080' },
  { value: 'building',  label: '🔨 Building',  color: '#e67e22' },
  { value: 'complete',  label: '✅ Complete',  color: '#27ae60' },
  { value: 'retired',   label: '📦 Retired',   color: '#7f8c8d' },
];

const STATUS_MAP = Object.fromEntries(STATUSES.map(s => [s.value, s]));

const GUILD_NAMES: Record<string, string> = {
  'W': 'Mono White', 'U': 'Mono Blue', 'B': 'Mono Black',
  'R': 'Mono Red',   'G': 'Mono Green',
  'WU': 'Azorius',  'UB': 'Dimir',   'BR': 'Rakdos',
  'RG': 'Gruul',    'WG': 'Selesnya', 'WB': 'Orzhov',
  'UR': 'Izzet',    'BG': 'Golgari',  'WR': 'Boros',
  'UG': 'Simic',
  'WUB': 'Esper',   'UBR': 'Grixis',  'BRG': 'Jund',
  'WRG': 'Naya',    'WUG': 'Bant',    'WBG': 'Abzan',
  'WUR': 'Jeskai',  'UBG': 'Sultai',  'WBR': 'Mardu',
  'URG': 'Temur',
  'WUBR': 'Non-Green', 'UBRG': 'Non-White', 'WBRG': 'Non-Blue',
  'WURG': 'Non-Black', 'WUBG': 'Non-Red',
  'WUBRG': 'Five Colour', 'C': 'Colorless', '': 'Colorless',
};

const WUBRG = [
  { code: 'W', token: '{W}' },
  { code: 'U', token: '{U}' },
  { code: 'B', token: '{B}' },
  { code: 'R', token: '{R}' },
  { code: 'G', token: '{G}' },
];

// Full ordered list of all 32 color identity combinations
const ALL_IDENTITIES = [
  'W','U','B','R','G',
  'WU','UB','BR','RG','WG','WB','UR','BG','WR','UG',
  'WUB','UBR','BRG','WRG','WUG','WBG','WUR','UBG','WBR','URG',
  'WUBR','UBRG','WBRG','WURG','WUBG',
  'WUBRG','C',
];

interface Deck {
  deckId:        number;
  name:          string;
  format:        string | null;
  description:   string | null;
  playNotes:     string | null;
  status:        string;
  colorIdentity: string;
  deckImageUrl:  string | null;
  totalCards:    number;
  mainboardCards:number;
  sideboardCards:number;
  estimatedValue:number;
  lastModified:  string;
}

function DeckBannerOverlay({ identity, symbols }: { identity: string; symbols: Record<string, any> }) {
  const guildName = GUILD_NAMES[identity] || GUILD_NAMES[''] || '';
  const isColorless = !identity || identity === 'C';
  const pips = isColorless
    ? [{ code: 'C', token: '{C}' }]
    : WUBRG.filter(c => identity.includes(c.code));
  return (
    <>
      {guildName && <div className="deck-banner-guild">{guildName}</div>}
      {pips.length > 0 && (
        <div className="deck-banner-pips">
          {pips.map(c => {
            const sym = symbols[c.token];
            return sym?.imageUrl
              ? <img key={c.code} src={sym.imageUrl} alt={c.code} className="deck-banner-pip" />
              : <span key={c.code} className="deck-banner-pip-text">{c.code}</span>;
          })}
        </div>
      )}
    </>
  );
}

// ── Deck Sidebar ──────────────────────────────────────────────────────────────

interface SidebarProps {
  decks: Deck[];
  symbols: Record<string, any>;
  selectedIdentity: string | null;
  selectedStatus: string | null;
  onSelectIdentity: (id: string | null) => void;
  onSelectStatus: (s: string | null) => void;
  onNavigateDeck: (deckId: number) => void;
  onPinChange: (pinned: boolean) => void;
}

function DeckSidebar({
  decks, symbols, selectedIdentity, selectedStatus,
  onSelectIdentity, onSelectStatus, onNavigateDeck, onPinChange
}: SidebarProps) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(() => {
    try { return localStorage.getItem('deckSidebarPinned') === 'true'; } catch { return false; }
  });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const sidebarRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    onPinChange(pinned);
    if (pinned) setOpen(true);
  }, [pinned, onPinChange]);

  const openSidebar  = () => { if (closeTimer.current) clearTimeout(closeTimer.current); setOpen(true); };
  const closeSidebar = () => { if (pinned) return; closeTimer.current = setTimeout(() => setOpen(false), 200); };
  const cancelClose  = () => { if (closeTimer.current) clearTimeout(closeTimer.current); };

  const togglePin = () => {
    const next = !pinned;
    setPinned(next);
    try { localStorage.setItem('deckSidebarPinned', String(next)); } catch {}
    if (!next) setOpen(false);
  };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (pinned) return;
      if (sidebarRef.current && !sidebarRef.current.contains(e.target as Node) &&
          triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [pinned]);

  // Build groups for all 32 identities — those with decks first, zeros last
  const groups = React.useMemo(() => {
    const map: Record<string, Deck[]> = {};
    decks.forEach(d => {
      const key = d.colorIdentity || 'C';
      if (!map[key]) map[key] = [];
      map[key].push(d);
    });
    // All 32 combinations — with decks first (sorted by count desc), then zeros
    const withDecks = ALL_IDENTITIES
      .filter(id => (map[id]?.length || 0) > 0)
      .sort((a, b) => (map[b]?.length || 0) - (map[a]?.length || 0));
    const withoutDecks = ALL_IDENTITIES
      .filter(id => (map[id]?.length || 0) === 0);
    return [...withDecks, ...withoutDecks].map(id => [id, map[id] || []] as [string, Deck[]]);
  }, [decks]);

  const toggleExpand = (identity: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(identity) ? next.delete(identity) : next.add(identity);
      return next;
    });
  };

  return (
    <>
      <div
        ref={triggerRef}
        className={`deck-sidebar-trigger ${open ? 'hidden' : ''}`}
        onMouseEnter={openSidebar}
        title="Open deck browser"
      >
        <div className="trigger-icon">🃏</div>
        {(selectedIdentity || selectedStatus) && <div className="trigger-dot" />}
      </div>

      <div
        ref={sidebarRef}
        className={`deck-sidebar ${open ? 'open' : ''} ${pinned ? 'pinned' : ''}`}
        onMouseEnter={cancelClose}
        onMouseLeave={closeSidebar}
      >
        {/* Header */}
        <div className="deck-sidebar-header">
          <span className="deck-sidebar-title">Browse</span>
          <div className="deck-sidebar-actions">
            <button className={`pin-btn ${pinned ? 'active' : ''}`}
              onClick={togglePin} title={pinned ? 'Unpin' : 'Pin open'}>📌</button>
            {!pinned && <button className="close-btn" onClick={() => setOpen(false)}>✕</button>}
          </div>
        </div>

        <div className="deck-sidebar-body">

          {/* Status filter */}
          <div className="deck-sidebar-section">
            <div className="deck-sidebar-section-label">Status</div>
            <div className="deck-sidebar-status-pills">
              <button
                className={`deck-sb-status-pill ${!selectedStatus ? 'active' : ''}`}
                onClick={() => onSelectStatus(null)}>
                All
              </button>
              {STATUSES.map(s => (
                <button key={s.value}
                  className={`deck-sb-status-pill ${selectedStatus === s.value ? 'active' : ''}`}
                  style={{ '--sc': s.color } as React.CSSProperties}
                  onClick={() => onSelectStatus(selectedStatus === s.value ? null : s.value)}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* All decks */}
          <div className="deck-sidebar-section">
            <button
              className={`deck-sb-all ${!selectedIdentity ? 'active' : ''}`}
              onClick={() => onSelectIdentity(null)}>
              All Decks
              <span className="deck-sb-count">{decks.length}</span>
            </button>
          </div>

          {/* Identity groups */}
          <div className="deck-sidebar-section">
            <div className="deck-sidebar-section-label">By Color</div>
            {groups.map(([identity, groupDecks]) => {
              const guildName = GUILD_NAMES[identity] || 'Colorless';
              const isExpanded = expanded.has(identity);
              const isSelected = selectedIdentity === identity;
              const imageUrl = groupDecks[0]?.deckImageUrl || null;
              const isColorless = !identity || identity === 'C';
              const pips = isColorless
                ? [{ code: 'C', token: '{C}' }]
                : WUBRG.filter(c => identity.includes(c.code));

              return (
                <div key={identity} className={`deck-sb-group ${isSelected ? 'selected' : ''} ${groupDecks.length === 0 ? 'empty' : ''}`}>
                  {/* Group header */}
                  <div className="deck-sb-group-header"
                       onClick={() => groupDecks.length > 0 && onSelectIdentity(isSelected ? null : identity)}>
                    {/* Mini banner */}
                    <div className="deck-sb-mini-banner">
                      {imageUrl
                        ? <img src={imageUrl} alt={guildName} className="deck-sb-mini-img" />
                        : <div className="deck-sb-mini-placeholder" />}
                      <div className="deck-sb-mini-pips">
                        {pips.map(c => {
                          const sym = symbols[c.token];
                          return sym?.imageUrl
                            ? <img key={c.code} src={sym.imageUrl} alt={c.code}
                                   className="deck-sb-pip" />
                            : <span key={c.code} className="deck-sb-pip-text">{c.code}</span>;
                        })}
                      </div>
                    </div>
                    {/* Guild name + count */}
                    <div className="deck-sb-group-info">
                      <span className="deck-sb-guild-name">{guildName}</span>
                      <span className={`deck-sb-count ${groupDecks.length === 0 ? 'zero' : ''}`}>
                        {groupDecks.length}
                      </span>
                    </div>
                    {/* Expand toggle — only shown when decks exist */}
                    {groupDecks.length > 0 && (
                      <button className="deck-sb-expand"
                              onClick={e => toggleExpand(identity, e)}
                              title={isExpanded ? 'Collapse' : 'Expand'}>
                        {isExpanded ? '−' : '+'}
                      </button>
                    )}
                  </div>

                  {/* Expanded deck list */}
                  {isExpanded && (
                    <div className="deck-sb-deck-list">
                      {groupDecks.map(d => (
                        <div key={d.deckId}
                             className="deck-sb-deck-item"
                             onClick={e => { e.stopPropagation(); onNavigateDeck(d.deckId); }}>
                          <span className="deck-sb-deck-name" title={d.name}>
                            {d.name.length > 18 ? d.name.slice(0, 18) + '…' : d.name}
                          </span>
                          <span className="deck-sb-deck-cards">{d.mainboardCards}</span>
                          {d.status && STATUS_MAP[d.status] && (
                            <span className="deck-sb-deck-status"
                                  style={{ color: STATUS_MAP[d.status].color }}>
                              {STATUS_MAP[d.status].label.split(' ')[0]}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

        </div>
      </div>
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function DecksPage() {
  const navigate = useNavigate();
  const symbols  = useManaSymbols();
  const [decks,        setDecks]        = useState<Deck[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [sidebarPinned, setSidebarPinned] = useState(false);
  const [selectedIdentity, setSelectedIdentity] = useState<string | null>(null);
  const [selectedStatus,   setSelectedStatus]   = useState<string | null>(null);
  const [showNew,      setShowNew]      = useState(false);
  const [newName,      setNewName]      = useState('');
  const [newFormat,    setNewFormat]    = useState('');
  const [newDesc,      setNewDesc]      = useState('');
  const [newStatus,    setNewStatus]    = useState('draft');
  const [newPlayNotes, setNewPlayNotes] = useState('');
  const [saving,       setSaving]       = useState(false);
  const [editDeck,     setEditDeck]     = useState<Deck | null>(null);

  const fetchDecks = async () => {
    try {
      const res = await axios.get(`${API}/decks`);
      setDecks(res.data);
    } catch (e) {
      console.error('Failed to fetch decks:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDecks(); }, []);

  // Filtered decks
  const filteredDecks = decks.filter(d => {
    if (selectedIdentity) {
      const deckIdentity = d.colorIdentity || 'C';
      if (deckIdentity !== selectedIdentity) return false;
    }
    if (selectedStatus && d.status !== selectedStatus) return false;
    return true;
  });

  const createDeck = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/decks`, {
        name: newName.trim(), format: newFormat || null,
        description: newDesc || null, status: newStatus,
        playNotes: newPlayNotes || null,
      });
      setShowNew(false);
      setNewName(''); setNewFormat(''); setNewDesc('');
      setNewStatus('draft'); setNewPlayNotes('');
      fetchDecks();
    } catch (e) { console.error('Failed to create deck:', e); }
    finally { setSaving(false); }
  };

  const saveDeckEdit = async () => {
    if (!editDeck || !editDeck.name.trim()) return;
    setSaving(true);
    try {
      await axios.put(`${API}/decks/${editDeck.deckId}`, {
        name: editDeck.name, format: editDeck.format || null,
        description: editDeck.description || null,
        status: editDeck.status || 'draft',
        playNotes: editDeck.playNotes || null,
      });
      setEditDeck(null);
      fetchDecks();
    } catch (e) { console.error('Failed to update deck:', e); }
    finally { setSaving(false); }
  };

  const deleteDeck = async (deckId: number, name: string) => {
    if (!window.confirm(`Delete deck "${name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/decks/${deckId}`);
      fetchDecks();
    } catch (e) { console.error('Failed to delete deck:', e); }
  };

  return (
    <div className={`decks-page ${sidebarPinned ? 'sidebar-pinned' : ''}`}>

      <DeckSidebar
        decks={decks}
        symbols={symbols}
        selectedIdentity={selectedIdentity}
        selectedStatus={selectedStatus}
        onSelectIdentity={setSelectedIdentity}
        onSelectStatus={setSelectedStatus}
        onNavigateDeck={id => navigate(`/decks/${id}`)}
        onPinChange={setSidebarPinned}
      />

      <div className="decks-content">
        <div className="decks-header">
          <div className="decks-header-left">
            <h1 className="decks-title">Decks</h1>
            {(selectedIdentity || selectedStatus) && (
              <span className="decks-filter-active">
                {selectedIdentity ? (GUILD_NAMES[selectedIdentity] || selectedIdentity) : ''}
                {selectedIdentity && selectedStatus ? ' · ' : ''}
                {selectedStatus ? STATUS_MAP[selectedStatus]?.label : ''}
                <button onClick={() => { setSelectedIdentity(null); setSelectedStatus(null); }}>✕</button>
              </span>
            )}
          </div>
          <button className="decks-new-btn" onClick={() => setShowNew(true)}>+ New Deck</button>
        </div>

        {/* New deck modal */}
        {showNew && (
          <div className="deck-modal-overlay" onClick={() => setShowNew(false)}>
            <div className="deck-modal" onClick={e => e.stopPropagation()}>
              <h2 className="deck-modal-title">New Deck</h2>
              <div className="deck-form">
                <label>Name *</label>
                <input value={newName} onChange={e => setNewName(e.target.value)}
                       placeholder="e.g. Mono Red Burn" autoFocus
                       onKeyDown={e => e.key === 'Enter' && createDeck()} />
                <label>Format</label>
                <select value={newFormat} onChange={e => setNewFormat(e.target.value)}>
                  <option value="">— Select format —</option>
                  {FORMATS.map(f => <option key={f} value={f.toLowerCase()}>{f}</option>)}
                </select>
                <label>Status</label>
                <select value={newStatus} onChange={e => setNewStatus(e.target.value)}>
                  {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <label>Short Description</label>
                <textarea value={newDesc} onChange={e => setNewDesc(e.target.value)}
                          placeholder="Brief description shown on the deck card..." rows={2} />
                <label>How to Play</label>
                <textarea value={newPlayNotes} onChange={e => setNewPlayNotes(e.target.value)}
                          placeholder="Strategy, win conditions, key combos..." rows={4} />
              </div>
              <div className="deck-modal-actions">
                <button className="deck-btn-cancel" onClick={() => setShowNew(false)}>Cancel</button>
                <button className="deck-btn-save" onClick={createDeck}
                        disabled={saving || !newName.trim()}>
                  {saving ? 'Creating…' : 'Create Deck'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit deck modal */}
        {editDeck && (
          <div className="deck-modal-overlay" onClick={() => setEditDeck(null)}>
            <div className="deck-modal" onClick={e => e.stopPropagation()}>
              <h2 className="deck-modal-title">Edit Deck</h2>
              <div className="deck-form">
                <label>Name *</label>
                <input value={editDeck.name}
                       onChange={e => setEditDeck({...editDeck, name: e.target.value})}
                       autoFocus />
                <label>Format</label>
                <select value={editDeck.format || ''}
                        onChange={e => setEditDeck({...editDeck, format: e.target.value || null})}>
                  <option value="">— Select format —</option>
                  {FORMATS.map(f => <option key={f} value={f.toLowerCase()}>{f}</option>)}
                </select>
                <label>Status</label>
                <select value={editDeck.status || 'draft'}
                        onChange={e => setEditDeck({...editDeck, status: e.target.value})}>
                  {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                <label>Short Description</label>
                <textarea value={editDeck.description || ''}
                          onChange={e => setEditDeck({...editDeck, description: e.target.value || null})}
                          rows={2} />
                <label>How to Play</label>
                <textarea value={editDeck.playNotes || ''}
                          onChange={e => setEditDeck({...editDeck, playNotes: e.target.value || null})}
                          rows={4} />
              </div>
              <div className="deck-modal-actions">
                <button className="deck-btn-cancel" onClick={() => setEditDeck(null)}>Cancel</button>
                <button className="deck-btn-save" onClick={saveDeckEdit}
                        disabled={saving || !editDeck.name.trim()}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Deck grid */}
        {loading ? (
          <div className="decks-loading">Loading decks…</div>
        ) : filteredDecks.length === 0 ? (
          <div className="decks-empty">
            <div className="decks-empty-icon">🃏</div>
            <div className="decks-empty-title">
              {decks.length === 0 ? 'No decks yet' : 'No decks match this filter'}
            </div>
            {decks.length === 0 && (
              <>
                <div className="decks-empty-sub">Create your first deck to get started</div>
                <button className="decks-new-btn" onClick={() => setShowNew(true)}>+ New Deck</button>
              </>
            )}
          </div>
        ) : (
          <div className="decks-grid">
            {filteredDecks.map(deck => (
              <div key={deck.deckId} className="deck-card"
                   onClick={() => navigate(`/decks/${deck.deckId}`)}>

                {deck.deckImageUrl && (
                  <div className="deck-card-image-wrap">
                    <img src={deck.deckImageUrl} alt={deck.name} className="deck-card-image" />
                    <DeckBannerOverlay identity={deck.colorIdentity} symbols={symbols} />
                  </div>
                )}

                <div className="deck-card-header">
                  <div className="deck-card-name">{deck.name}</div>
                  <div className="deck-card-badges">
                    {deck.format && (
                      <span className="deck-format-badge">
                        {deck.format.charAt(0).toUpperCase() + deck.format.slice(1)}
                      </span>
                    )}
                    {deck.status && STATUS_MAP[deck.status] && (
                      <span className="deck-status-badge"
                            style={{ color: STATUS_MAP[deck.status].color,
                                     borderColor: STATUS_MAP[deck.status].color + '40' }}>
                        {STATUS_MAP[deck.status].label}
                      </span>
                    )}
                  </div>
                </div>

                {deck.description && (
                  <div className="deck-card-desc">{deck.description}</div>
                )}

                <div className="deck-card-stats">
                  <div className="deck-stat">
                    <span className="deck-stat-value">{deck.mainboardCards}</span>
                    <span className="deck-stat-label">Mainboard</span>
                  </div>
                  {deck.sideboardCards > 0 && (
                    <div className="deck-stat">
                      <span className="deck-stat-value">{deck.sideboardCards}</span>
                      <span className="deck-stat-label">Sideboard</span>
                    </div>
                  )}
                  <div className="deck-stat">
                    <span className="deck-stat-value">${deck.estimatedValue.toFixed(0)}</span>
                    <span className="deck-stat-label">Est. Value</span>
                  </div>
                </div>

                <div className="deck-card-footer">
                  <span className="deck-card-date">
                    {new Date(deck.lastModified).toLocaleDateString()}
                  </span>
                  <div className="deck-card-actions" onClick={e => e.stopPropagation()}>
                    <button className="deck-action-btn"
                            onClick={() => navigate(`/decks/${deck.deckId}/build`)}
                            title="Open deck builder">⊞</button>
                    <button className="deck-action-btn"
                            onClick={() => setEditDeck({...deck})}
                            title="Edit deck">✎</button>
                    <button className="deck-action-btn deck-action-delete"
                            onClick={() => deleteDeck(deck.deckId, deck.name)}
                            title="Delete deck">✕</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
